/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

/**
 * Debounce *invocations* of a callback.
 *
 * Differences vs `useDebounce`:
 * - `useDebounce` debounces a value update.
 * - `useDebouncedCallback` debounces calling a function.
 *
 * Notes:
 * - The returned function is stable (does not change across renders) unless `delay` changes.
 * - The latest version of `callback` is always used (no stale closures).
 * - Includes `cancel` and `flush` helpers.
 */

// biome-ignore lint/suspicious/noExplicitAny: generic hook
export type DebouncedCallback<TArgs extends any[]> = ((...args: TArgs) => void) & {
  cancel: () => void;
  flush: () => void;
  pending: () => boolean;
};

// biome-ignore lint/suspicious/noExplicitAny: generic hook
export function useDebouncedCallback<TArgs extends any[]>(
  callback: (...args: TArgs) => void,
  delay = 500,
): DebouncedCallback<TArgs> {
  const callbackRef = useRef(callback);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastArgsRef = useRef<TArgs | null>(null);

  // Always call the latest callback without changing the debounced function identity.
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    lastArgsRef.current = null;
  }, []);

  // Clear any pending timer when delay changes or component unmounts.
  // biome-ignore lint/correctness/useExhaustiveDependencies: should clear timer on delay change
  useEffect(() => {
    return cancel;
  }, [delay]);

  const flush = useCallback(() => {
    if (!timerRef.current || !lastArgsRef.current) {
      return;
    }

    const args = lastArgsRef.current;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    lastArgsRef.current = null;

    callbackRef.current(...args);
  }, []);

  const pending = useCallback(() => timerRef.current != null, []);

  const debounced = useCallback(
    (...args: TArgs) => {
      lastArgsRef.current = args;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;

        const latestArgs = lastArgsRef.current;
        lastArgsRef.current = null;

        if (latestArgs) {
          callbackRef.current(...latestArgs);
        }
      }, delay);
    },
    [delay],
  );

  return useMemo(() => {
    const fn = debounced as DebouncedCallback<TArgs>;
    fn.cancel = cancel;
    fn.flush = flush;
    fn.pending = pending;
    return fn;
  }, [cancel, debounced, flush, pending]);
}
